import { PaperCompanyValues } from "enums/PaperCompanyValues";
import { StorageKeys } from "enums/StorageKeys";
import { useEffect, useState } from "react";
import useUtilities from "./useUtilities";

type CompanyValueConfig<T extends string = string> = {
    key: PaperCompanyValues;
    defaultValue?: T;
};

type CompanyValuesResult<T extends Record<string, CompanyValueConfig>> = {
    [K in keyof T]: string;
} & {
    isLoading: boolean;
};

/**
 * Custom hook to retrieve and monitor paper company values from session storage.
 * Polls session storage every 200ms until values are available.
 *
 * @param config - Object mapping result keys to PaperCompanyValues keys
 * @returns Object with requested company values and isLoading flag
 *
 * @example
 * // Single value
 * const { phoneNumber, isLoading } = usePaperCompanyValues({
 *   phoneNumber: { key: PaperCompanyValues.CustomerPortalPhoneNumber }
 * });
 *
 * @example
 * // Multiple values
 * const { companyName, phoneNumber, isLoading } = usePaperCompanyValues({
 *   companyName: { key: PaperCompanyValues.CompanyName },
 *   phoneNumber: { key: PaperCompanyValues.CustomerPortalPhoneNumber }
 * });
 */
const usePaperCompanyValues = <T extends Record<string, CompanyValueConfig>>(
    config: T
): CompanyValuesResult<T> => {
    const { getPaperCompanyValuesByKey } = useUtilities();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [values, setValues] = useState<Record<string, string>>(() => {
        // Initialize with default values if provided
        const initialValues: Record<string, string> = {};
        Object.entries(config).forEach(([resultKey, valueConfig]: [string, CompanyValueConfig]) => {
            initialValues[resultKey] = valueConfig.defaultValue || "";
        });
        return initialValues;
    });

    useEffect(() => {
        const checkSessionForCompanyValues = (): void => {
            const paperCompanyValues: string | null = sessionStorage.getItem(
                StorageKeys.PaperCompanyValues
            );

            if (paperCompanyValues) {
                const newValues: Record<string, string> = {};
                let hasValues = false;

                Object.entries(config).forEach(
                    ([resultKey, valueConfig]: [string, CompanyValueConfig]) => {
                        const value: string | null = getPaperCompanyValuesByKey(valueConfig.key);
                        if (value) {
                            newValues[resultKey] = value;
                            hasValues = true;
                        } else {
                            newValues[resultKey] = valueConfig.defaultValue || "";
                        }
                    }
                );

                if (hasValues) {
                    setValues(newValues);
                    setIsLoading(false);
                    clearInterval(interval);
                }
            }
        };

        checkSessionForCompanyValues();

        const interval: NodeJS.Timeout = setInterval(checkSessionForCompanyValues, 200);

        return () => clearInterval(interval);
    }, []);

    return { ...values, isLoading } as CompanyValuesResult<T>;
};

export default usePaperCompanyValues;
